import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';

import { environment } from 'environments/environment';
import { SalesSummary } from 'app/core/models/sales-summary.model';

/**
 * Home sales dashboard data. Separate from the instance analytics call because
 * it is gated on a different permission (Offers.View, not
 * Instances.ViewDashboard) and a user may hold one without the other.
 */
@Injectable({ providedIn: 'root' })
export class DashboardService {
  private _httpClient: HttpClient = inject(HttpClient);
  /** Same prefix every other service uses — note it is `apiUrlPrefix`, and it
   *  already carries its trailing slash. */
  private _apiUrl = environment.apiUrlPrefix;
  private _dashboardUrl = 'Dashboard';

  /**
   * Fetches the sales summary for an explicit window.
   *
   * The endpoint also accepts a trailing length in months and answers the same
   * shape, but a length always ends today — a closed calendar year is only
   * reachable with both bounds. Sending dates keeps the window the client asked
   * for rather than one the server derives.
   *
   * @param range Inclusive `YYYY-MM-DD` bounds; at most 24 calendar months apart.
   * @returns The summary, or null when the caller lacks the permission or the
   *   request fails — the cards render their empty state rather than an
   *   endless skeleton.
   */
  getSalesSummary(range: { from: string; to: string }): Observable<SalesSummary | null> {
    return this._httpClient
      .get<SalesSummary>(`${this._apiUrl}${this._dashboardUrl}/sales-summary`, {
        params: { from: range.from, to: range.to },
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 403) {
            console.warn('getSalesSummary: acceso denegado (403)');
          } else {
            console.error('getSalesSummary: error inesperado', error);
          }
          return of(null);
        })
      );
  }
}
